import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { BaseURL } from "../../APIs/APIUrls";
import { GetAllPageBannerforWeb } from "../../APIs/APIHelperforWeb";

export default function PageTitle2({ setMetaTitle, setMetaDescription, setMetaKeywords, setMetaImage }) {
  const { pathname } = useLocation();
  const fallbackBanner = {
    pageName: "Pricing",
    image: "/images/page-title/page-title-6.jpg",
  };
  const [banner, setBanner] = useState(fallbackBanner);

  const normalizePath = (value = "") => {
    const rawValue = String(value || "").trim();
    if (!rawValue) return "/";

    let pathValue = rawValue;
    try {
      if (/^https?:\/\//i.test(rawValue)) {
        pathValue = new URL(rawValue).pathname;
      }
    } catch (error) {
      pathValue = rawValue;
    }

    const withoutQueryOrHash = decodeURIComponent(pathValue)
      .split(/[?#]/)[0]
      .trim()
      .toLowerCase();
    if (!withoutQueryOrHash) return "/";

    const withLeadingSlash = withoutQueryOrHash.startsWith("/")
      ? withoutQueryOrHash
      : `/${withoutQueryOrHash}`;

    const compactSlashes = withLeadingSlash.replace(/\/+/g, "/");
    return compactSlashes.length > 1
      ? compactSlashes.replace(/\/+$/, "")
      : compactSlashes;
  };

  const getPageNameFromPath = (path) => {
    const cleanPath = normalizePath(path);
    const segment = cleanPath.split("/").filter(Boolean).pop() || "page";
    return segment
      .replace(/[-_]+/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const resolveImageUrl = (imagePath = "") => {
    const value = String(imagePath || "").trim();
    if (!value) return fallbackBanner.image;
    if (/^https?:\/\//i.test(value) || value.startsWith("data:")) {
      return value;
    }
    const normalizedPath = value.startsWith("/") ? value : `/${value}`;
    return `${BaseURL.replace(/\/+$/, "")}${normalizedPath}`;
  };

  const getBestBannerMatch = (rows, activePath) => {
    if (!Array.isArray(rows) || rows.length === 0) return null;

    const scoredMatches = rows
      .map((item) => {
        const bannerPath = normalizePath(item?.PageURL);
        if (!bannerPath || bannerPath === "/") {
          return null;
        }

        let score = 0;
        if (bannerPath === activePath) {
          score = 4;
        } else if (activePath.startsWith(`${bannerPath}/`)) {
          score = 3;
        } else if (bannerPath.startsWith(`${activePath}/`)) {
          score = 2;
        } else {
          const activeSlug = activePath.split("/").filter(Boolean).pop();
          const bannerSlug = bannerPath.split("/").filter(Boolean).pop();
          if (activeSlug && bannerSlug && activeSlug === bannerSlug) {
            score = 1;
          }
        }

        if (!score) return null;
        return { item, score, pathLength: bannerPath.length };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score || b.pathLength - a.pathLength);

    return scoredMatches[0]?.item || null;
  };

  useEffect(() => {
    const fetchPageBanner = async () => {
      const rows = await GetAllPageBannerforWeb();
      const activePath = normalizePath(pathname);
      const matchedBanner = getBestBannerMatch(rows, activePath);
      const metaTitle = matchedBanner?.MetaTitle || `${getPageNameFromPath(activePath)} | AskMeRealty`;
      const metaDescription = matchedBanner?.MetaDescription || "AskMeRealty - Contact us for any inquiries, support, or feedback. We're here to assist you with your real estate needs.";
      const metaKeywords = matchedBanner?.MetaKeyword || "Contact Us, AskMeRealty, Inquiries, Support, Feedback, Real Estate Assistance";
      const metaImage = matchedBanner?.Image ? resolveImageUrl(matchedBanner.Image) : "";

      if (matchedBanner) {
        setBanner({
          pageName: matchedBanner?.PageName || getPageNameFromPath(activePath),
          image: resolveImageUrl(matchedBanner?.Image),
          alttext: matchedBanner?.ImageAltText || getPageNameFromPath(activePath),
        });
        setMetaTitle(metaTitle);
        setMetaDescription(metaDescription);
        setMetaKeywords(metaKeywords);
        setMetaImage(metaImage);
      } else {
        setBanner({
          pageName: getPageNameFromPath(activePath),
          image: fallbackBanner.image,
          alttext: getPageNameFromPath(activePath),
        });
      }
    };

    fetchPageBanner();
  }, [pathname]);

  return (
    <section className="flat-title-page flat-title-page--image">
      <img
        className="flat-title-page__image"
        src={banner.image}
        alt={banner.alttext || banner.pageName}
      />
      <div className="container">
        <div className="breadcrumb-content">
          <ul className="breadcrumb">
            <li>
              <Link to={`/`} className="text-white">
                Home
              </Link>
            </li>
            <li className="text-white">/ Pages</li>
            <li className="text-white">/ {banner.pageName}</li>
          </ul>
          <h1 className="text-center text-white title">{banner.pageName}</h1>
        </div>
      </div>
    </section>
  );
}
